import { LazyLoadEvent } from 'primeng/api';
import {
  CommonLookupItem,
  ProviderWebsiteItem,
} from '../../shared/models/common-lookup-item.model';
import { JobseekerFilterModel } from '../shared/models/jobseeker-filter-model';
import { Component, DestroyRef, OnInit } from '@angular/core';
import { LookupService } from '@admin/shared/services/lookup.service';
import { JobseekerModel } from '@admin/jobseekers/shared/models/jobseeker-model';
import { JobseekerService } from '@admin/jobseekers/shared/services/jobseeker.service';
import { PagingFilter } from '@admin/shared/models/paging-filter.model';
import { ConfigurationService } from '@admin/shared/services/configuration.service';
import { OccupationCategory } from '@admin/shared/models/occupation-category.model';
import { Utils, MonthBreakpoint } from '@admin/shared/utils';
import { GenericResult } from '@admin/shared/models/generic-result';
import { CustomMessageService } from '@admin/core/message.service';
import { ApplicationMessage } from '@admin/core/models/application-message';
import { MessageType } from '@admin/core/models/enum/message-type';
import { ApplicationMessageSource } from '@admin/core/models/application-message-source';
import { Table } from 'primeng/table';
import { SelectAnnouncementStatuses } from '@admin/announcements/shared/models/announcement-detail-model';
import { JobseekerStatusEnum } from '../shared/models/jobseeker-status-enum';
import dayjs from 'dayjs';
import { FormControl } from '@angular/forms';
import { WorldLanguageModel } from '@admin/shared/models/world-language-model';
import { DocumentItemModel } from '../shared/models/jobseeker-moderation.model';
import { EducationalInstitutionModel } from '@admin/shared/models/educational-institution-model';
import { EducationalProgramModel } from '@admin/shared/models/educational-program-model';
import { EducationLevelModel } from '@admin/shared/models/education-level-model';
import { TranslateService } from '@ngx-translate/core';
import { Specialization } from '@admin/shared/models/specializations.model';
import { AuthService } from '@admin/core/auth.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-jobseeker-list',
  templateUrl: './jobseeker-list.component.html',
  styleUrls: ['./jobseeker-list.component.less'],
  standalone: false,
})
export class JobseekerListComponent implements OnInit {
  public filterPagingWrapper: PagingFilter<JobseekerFilterModel>;

  public extendedLocationPath = new FormControl(null);

  public localities: CommonLookupItem<number, string>[];
  public drivingLicenses: CommonLookupItem<number, string>[];
  public profileStates: CommonLookupItem<number, string>[];

  public selectedDrivingLicenses: CommonLookupItem<number, string>[];
  public selectedLocations: CommonLookupItem<number, string>[];

  public selectedLocationsPath: any;

  public selectedLanguages: WorldLanguageModel[];
  public languageOptions: CommonLookupItem<number, string>[];

  public selectedInstitutions: EducationalInstitutionModel[];
  public selectedLevels: { value: string; name: string }[];
  public selectedPrograms: EducationalProgramModel[];

  public providerWebsiteList: CommonLookupItem<number, string>[];

  public activeProviderWebsiteList: ProviderWebsiteItem[];

  public mainProviderWebsite: ProviderWebsiteItem;

  public activeEvents: CommonLookupItem<any, any>[];

  public switchModerationStatuses: SelectAnnouncementStatuses[];

  public jobseekerList: JobseekerModel[];

  public totalModeration: number = null;

  public lastSelectedDrivingLicenceFilter: number = null;
  public lastSelectedLocationFilter: number = null;

  public totalRecords: number;

  public universityList: any;
  public educationLevelList: any;
  public drivingLicenceList: any;
  public programList: any;

  public selectedVehicleTypes: { vehicleTypeId: number; name: string }[];
  public vehicleOptions: { vehicleTypeId: number; name: string }[];

  public lastLoadEvent: LazyLoadEvent;

  public dateFormat: string;
  public specialDateFormat: string;
  public pageSize: number;

  public selectedDocumentTypes: {
    jobseekerDocumentTypeId: number;
    name: string;
  }[];
  public documentOptions: { jobseekerDocumentTypeId: number; name: string }[];

  private isReset: boolean;

  private currentDate: Date;

  public locationLevelCount: number;
  public locationFirstLevelId: string;
  public locationRootId: string;
  public jobseekerLocationRequiredLevelCount: number;
  public jobseekerLocationLastHiddenLevelCount: number;

  public jobseekerStatusEnum: any;

  public categoryOptions: { label: string; value: OccupationCategory }[];
  public childCategoriesMap: { [index: number]: OccupationCategory };
  public selectedCategories: OccupationCategory[];

  public specializationOptions: { label: string; value: Specialization }[];
  public childSpecializationsMap: { [index: number]: Specialization };
  public selectedSpecializations: Specialization[];

  public providerWebsiteMap: { [index: string]: string } = {};
  public jobseekerExpandedRows = {};

  onLoad(event: LazyLoadEvent) {
    if (this.isReset) {
      this.jobseekerList = [];
      this.totalRecords = 0;
      return;
    }

    if (event.sortField && event.sortOrder) {
      this.filterPagingWrapper.sortParameters = [
        {
          column: event.sortField,
          order: event.sortOrder == 1 ? 'asc' : 'desc',
        },
      ];
    }

    this.filterPagingWrapper.pagingInfo.start = event.first;
    this.filterPagingWrapper.pagingInfo.limit = event.rows;
    this.lastLoadEvent = event;
    this.onFilter();
  }

  onFilter() {
    this.filterPagingWrapper.pagingInfo.start =
      this.filterPagingWrapper.pagingInfo.start || 0;

    this.jobseekerService
      .listJobseeker(this.filterPagingWrapper)
      .subscribe((d) => {
        this.totalRecords = d.totalCount;
        this.jobseekerList = d.items;
      });

    this.jobseekerExpandedRows = {};
    this.jobseekerService.getModerationJobseekerCount().subscribe((x) => {
      this.totalModeration = x.data.totalModerationCount;
    });
  }

  onApplyClick(dt: Table) {
    this.filterPagingWrapper.filter.drivingLicenses =
      this.selectedDrivingLicenses.map((p) => p.value);
    this.filterPagingWrapper.filter.locations = this.selectedLocations.map(
      (p) => p.value
    );
    this.filterPagingWrapper.filter.categories = this.selectedCategories.map(
      (c) => c.occupationCategoryId
    );
    this.filterPagingWrapper.filter.specializations =
      this.selectedSpecializations.map((c) => c.specializationId);
    this.filterPagingWrapper.filter.educationalInstitutionIdList =
      this.selectedInstitutions?.map(
        (institution) => institution.educationalInstitutionId
      ) || [];

    if (this.selectedLocationsPath != null) {
      let filteredLocations = this.selectedLocationsPath?.locations.filter(
        (loc) => loc.locationId !== undefined
      );

      this.filterPagingWrapper.filter.extendedLocationList = [
        filteredLocations[filteredLocations.length - 1]?.locationId,
      ];
    } else {
      this.filterPagingWrapper.filter.extendedLocationList = [];
    }

    this.filterPagingWrapper.filter.educationalProgramIdList =
      this.selectedPrograms?.map((p) => p.educationalProgramId) || [];

    this.filterPagingWrapper.filter.educationLevelIdList =
      this.selectedLevels?.map((l) => +l.value) || [];

    this.filterPagingWrapper.filter.languages =
      this.selectedLanguages.map((l) => l.worldLanguageId) || [];
    this.filterPagingWrapper.filter.vehicleTypes =
      this.selectedVehicleTypes.map((v) => v.vehicleTypeId);
    this.filterPagingWrapper.filter.documentTypes =
      this.selectedDocumentTypes.map((d) => d.jobseekerDocumentTypeId);
    this.filterPagingWrapper.pagingInfo.start = 0;

    //TODO: bug in paginator, does not update state correctly, change this code after bug is fixed in framework (primeng)
    this.isReset = true;
    dt.reset();
    this.isReset = false;

    this.onFilter();
  }

  public onExpandClick(event: any) {
    const jobseekerId = event.data.jobseekerId;

    this.jobseekerService.getJobseekerDetails(jobseekerId).subscribe((s) => {
      event.data.jobseekerDetails = s.data;
    });
  }

  onUnemployedCheck() {
    if (this.filterPagingWrapper.filter.isWihoutExperience) {
      this.filterPagingWrapper.filter.experienceFrom = null;
      this.filterPagingWrapper.filter.experienceTo = null;
    }
  }

  onHasDocumentCheck() {
    if (!this.filterPagingWrapper.filter.hasDocument) {
      this.selectedDocumentTypes = [];
    }
  }

  onOwnTransportCheck() {
    if (!this.filterPagingWrapper.filter.isVehicleOwner) {
      this.selectedVehicleTypes = [];
    }
  }

  onClearClick() {
    this.filterPagingWrapper = new PagingFilter(new JobseekerFilterModel());
    this.selectedDrivingLicenses = [];
    this.selectedLocations = [];
    this.selectedInstitutions = [];
    this.selectedLevels = [];
    this.selectedPrograms = [];
    this.selectedCategories = [];
    this.selectedSpecializations = [];
    this.selectedVehicleTypes = [];
    this.selectedLocationsPath = null;
    this.extendedLocationPath = new FormControl(null);
    this.selectedDocumentTypes = [];
    this.selectedLanguages = [];
    this.lastSelectedDrivingLicenceFilter = null;
  }

  onDrivingLicenceFilterChange() {
    this.onAccumulatingDropDownOptionChange(
      this.lastSelectedDrivingLicenceFilter,
      this.selectedDrivingLicenses,
      this.drivingLicenses,
      (p: any) => p.value
    );
  }

  onSelectedInstitutions(event) {
    this.selectedInstitutions = event;
  }

  onSelectedPrograms(event) {
    this.selectedPrograms = event;
  }

  onSelectedEducationLevels(event) {
    this.selectedLevels = event;
  }

  onSelectedLocationFilterRemoveClick = (item) => {
    this.onSelectedOptionRemoveClick(this.selectedLocations, item);
    this.lastSelectedLocationFilter = null;
  };

  onLocationFilterChange() {
    this.onAccumulatingDropDownOptionChange(
      this.lastSelectedLocationFilter,
      this.selectedLocations,
      this.localities,
      (p: any) => p.value
    );
  }

  onAccumulatingDropDownOptionChange(
    lastSelectedValue: number,
    selectedList: any[],
    lookupList: any[],
    valueField: (item: any) => any
  ) {
    if (lastSelectedValue == null) return;

    if (
      selectedList.filter((p) => valueField(p) == lastSelectedValue).length ===
      0
    ) {
      var item = lookupList.filter(
        (p) => valueField(p) == lastSelectedValue
      )[0];
      selectedList.push(item);
    }
  }

  onSelectedOptionRemoveClick(list, item: any) {
    var index = list.indexOf(item);
    list.splice(index, 1);
  }

  refresh() {
    this.onFilter();
  }

  onJobseekerDelete(jobseeker: JobseekerModel) {
    if (!confirm(this.translateService.instant('message.AreYouSure'))) {
      return;
    }
    this.jobseekerService
      .deleteJobseeker(jobseeker.jobseekerId)
      .subscribe((d) => {
        this.jobseekerDeleteResult(d);
      });
  }

  jobseekerDeleteResult(result: any) {
    if (result.success) {
      this.refresh();
    } else {
      this.messageService.pushMessage(<ApplicationMessage>{
        type: MessageType.Error,
        sourceId: ApplicationMessageSource.global,
        summary: '',
        detail: this.translateService.instant('message.' + result.error),
      });
    }
  }

  onSpecializationOptionRemoveClick(specialization: Specialization) {
    this.selectedSpecializations = this.selectedSpecializations.filter(
      (c) => c.specializationId != specialization.specializationId
    );
  }

  onSpecializationClick(event, specialization: Specialization) {
    if (specialization.isHead) {
      event.stopPropagation();
    }
  }

  switchLastVisitDateColor(visitDate: Date) {
    let date = this.currentDate;
    const diffInMonths = this.monthDiff(visitDate, date);

    if (diffInMonths < MonthBreakpoint.THREE) {
      return VisitDateColorEnum.LessThanThreeMonth;
    } else if (
      diffInMonths >= MonthBreakpoint.THREE &&
      diffInMonths < MonthBreakpoint.SIX
    ) {
      return VisitDateColorEnum.ThreeToSixMonth;
    } else if (
      diffInMonths >= MonthBreakpoint.SIX &&
      diffInMonths < MonthBreakpoint.TWELVE
    ) {
      return VisitDateColorEnum.SixToTwelveMonth;
    } else {
      return VisitDateColorEnum.MoreThanAYear;
    }
  }

  private monthDiff(dateFrom: Date, dateTo: Date): number {
    const dateFromConverted = dayjs(dateFrom);
    const dateToConverted = dayjs(dateTo);
    return dateToConverted.diff(dateFromConverted, 'month');
  }

  onLoginToPublicPortal(providerWebsiteId: number) {
    this.authService.loginToPublicPortal(providerWebsiteId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      //display message
    });
  }

  switchProfileStateColor(jobseekerProfileStatusId) {
    if (jobseekerProfileStatusId === ProfileStateEnum.Basic) {
      return ProfileStateColorEnum.Basic;
    } else if (jobseekerProfileStatusId === ProfileStateEnum.Incompleted) {
      return ProfileStateColorEnum.Incompleted;
    } else {
      return ProfileStateColorEnum.Completed;
    }
  }

  switchJobseekerStatusColor(jobseekerStatusId) {
    if (jobseekerStatusId === JobseekerStatusEnum.Active) {
      return JobseekerStatusColorEnum.Active;
    } else if (jobseekerStatusId === JobseekerStatusEnum.Pending) {
      return JobseekerStatusColorEnum.Pending;
    }
  }

  onSpecializationFilterChange(e) {
    if (e.itemValue.value.isHead) {
      this.onSpecializationOptionRemoveClick(e.itemValue.value);
    }
  }

  getSpecialDateFormat(format: string) {
    return format.replace('MM', 'mm').replace('yyyy', 'yy');
  }

  constructor(
    private lookupService: LookupService,
    private config: ConfigurationService,
    private jobseekerService: JobseekerService,
    private translateService: TranslateService,
    private messageService: CustomMessageService,
    private authService: AuthService,
    private destroyRef: DestroyRef
  ) {
    this.filterPagingWrapper = new PagingFilter(new JobseekerFilterModel());
    this.selectedDrivingLicenses = [];
    this.selectedLocations = [];
    this.localities = [];
    this.drivingLicenses = [];
    this.profileStates = [];
    this.selectedCategories = [];
    this.selectedSpecializations = [];
    this.selectedVehicleTypes = [];
    this.selectedDocumentTypes = [];
    this.selectedLanguages = [];
    this.selectedInstitutions = [];
    this.selectedLevels = null;
    this.selectedPrograms = [];
    this.jobseekerStatusEnum = JobseekerStatusEnum;
  }

  ngOnInit() {
    this.jobseekerService.getModerationJobseekerCount().subscribe((x) => {
      this.totalModeration = x.data.totalModerationCount;
    });
    this.currentDate = new Date();
    let lookupData = this.lookupService.getCachedLookupData();
    this.localities = lookupData.localities;
    this.drivingLicenses = lookupData.drivingLicenses;
    this.profileStates = lookupData.jobseekerProfileStates;
    this.providerWebsiteList = lookupData.providerWebsiteList;
    this.activeProviderWebsiteList = lookupData.activeProviderWebsiteList;
    this.mainProviderWebsite = this.activeProviderWebsiteList.find(
      (v) => v.isMain === true
    );
    this.loadActiveEvents();

    lookupData.providerWebsiteList.forEach((providerWebsite) => {
      this.providerWebsiteMap[
        providerWebsite.value
      ] = `https://${providerWebsite.text}/`;
    });

    this.educationLevelList = lookupData.educationLevelList;
    this.universityList = lookupData.educationalInstitutionsList;
    this.programList = lookupData.educationalPrograms;

    let configs = this.config.getCachedConfig();
    this.locationLevelCount = configs.locationLevelCount;
    this.locationFirstLevelId = configs.locationFirstLevelId;
    this.locationRootId = configs.locationRootId;
    this.jobseekerLocationRequiredLevelCount =
      configs.jobseekerLocationRequiredLevelCount;
    this.jobseekerLocationLastHiddenLevelCount =
      configs.jobseekerLocationLastHiddenLevelCount;

    this.dateFormat = configs.dateFormat; //TODO get from config
    this.specialDateFormat = this.getSpecialDateFormat(this.dateFormat);
    this.pageSize = configs.defaultPageSize; //TODO get from config

    this.categoryOptions = lookupData.occupationCategories
      .sort((a, b) => {
        const headsDiff = a.headId - b.headId;
        if (headsDiff != 0) {
          return headsDiff;
        }
        return a.isHeadOld() ? -1 : 1;
      })
      .map((c) => ({ label: c.name, value: c }));
    this.childCategoriesMap = Utils.toMap(
      this.lookupService.getChildCategories(lookupData.occupationCategories),
      (c) => c.occupationCategoryId
    );

    this.specializationOptions = lookupData.specializations
      .sort((a, b) => {
        const headsDiff = a.headId?.localeCompare(b.headId || '') || 0;
        if (headsDiff !== 0) {
          return headsDiff;
        }

        console.log(headsDiff);
        return a.isHead ? -1 : 1;
      })
      .map((c) => ({ label: c.name, value: c }));
    this.childSpecializationsMap = Utils.toMap(
      this.lookupService.getChildSpecializations(lookupData.specializations),
      (c) => c.specializationId
    );

    this.documentOptions = lookupData.jobseekerDocumentTypes;

    this.languageOptions = lookupData.worldLanguages;

    this.vehicleOptions = lookupData.vehicleTypes;

    this.onFilter();
    this.switchModerationStatuses = [
      {
        label: this.translateService.instant('label.All'),
        value: ModerationStatus.All,
      },
      {
        label: this.translateService.instant('label.New'),
        value: ModerationStatus.New,
      },
      {
        label: this.translateService.instant('label.Edited'),
        value: ModerationStatus.Edited,
      },
    ];
  }

  private loadActiveEvents() {
    this.jobseekerService.listOldAndCurrentEvents().subscribe((r) => {
      this.activeEvents = r;
    });
  }
}
export enum ModerationStatus {
  All = 1,
  New = 2,
  Edited = 3,
}

export enum VisitDateColorEnum {
  LessThanThreeMonth = '#85C226',
  ThreeToSixMonth = '#F7CD3A',
  SixToTwelveMonth = ' #FF6500',
  MoreThanAYear = '#CB2A2F',
}

export enum ProfileStateColorEnum {
  Basic = '#F7CD3A',
  Incompleted = '#F55753',
  Completed = '#85C226',
}

export enum JobseekerStatusColorEnum {
  Pending = '#FF6666',
  Active = '#85C226',
}

export enum ProfileStateEnum {
  Incompleted = 1,
  Basic = 2,
  Completed = 3,
}
